import {
  lazy,
  Suspense,
  useEffect,
  useState,
  type ComponentProps,
  type ComponentType,
  type ReactNode
} from "react";

import type {
  ProjectCostTelemetryResponse,
  ProjectReadinessV1,
  ReadinessBlockerV1,
  ScheduleEntry,
  SpecPolicyFlagSet
} from "@automatry/schemas";
import type { SourceCoverageWarning } from "./features/create/createDraft";
import type { RequirementsSurfaceProps } from "./features/requirementsEngine";
import type { DocumentRouteQuery } from "./appRoutes";
import type { WorkspaceMode } from "./app/types";
import type { SpecifyProjectInput } from "./app/specifyProject";
import {
  agentTraceForApartmentType,
  type ProjectDisplayData,
  type ProjectWorkspaceDisplayData
} from "./app/projectData";
import { reloadForLazyRouteRecovery } from "./app/lazyRouteRecovery";
import { RetryableLazyRoute } from "./components/LazyRouteBoundary";
import type { ScreenId } from "./nav";
import type { CarringtonEngineFixture } from "./impactPreview";
import type { ApartmentSolutionDisplayViewModel } from "./viewmodel/solutionViewAdapter";
import type {
  TenderIntakeProjectContext,
  TenderTakeoffReviewState
} from "./features/tender/TenderIngestionScreen";
import type { ExportScreenProps } from "./screens/ExportScreen";
import { InputsScreen } from "./screens/InputsScreen";
import { ProjectOverview } from "./screens/ProjectOverview";
import { ProjectLoadingScreen, SpecWorkspaceLoadingScreen } from "./screens/shared";
import { RequirementsScreen } from "./screens/RequirementsScreen";
import { RisksScreen } from "./screens/RisksScreen";
import { ScopeScreen } from "./screens/ScopeScreen";
import { SpecWorkspace, type SpecTab } from "./screens/SpecWorkspace";
import type { CompareMatrixProps } from "./screens/spec/CompareMatrix";
import { StubScreen } from "./screens/StubScreen";
import type { DownstreamConfidenceModel } from "./viewmodel/downstreamConfidenceTypes";
import type { ReadinessBannerProps } from "./components/ReadinessBanner";
import { TokenUsageScreen } from "./screens/TokenUsageScreen";
import type { AdminLlmSettingsScreen as AdminLlmSettingsScreenComponent } from "./screens/AdminLlmSettingsScreen";

const loadAdminLlmSettingsScreen = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./screens/AdminLlmSettingsScreen.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as typeof import("./screens/AdminLlmSettingsScreen"))
      : await import("./screens/AdminLlmSettingsScreen");

  return {
    default: module.AdminLlmSettingsScreen as ComponentType<
      ComponentProps<typeof AdminLlmSettingsScreenComponent>
    >
  };
};

export function AdminRouter({ section }: { section: "llm" }) {
  if (section === "llm") {
    return (
      <RetryableLazyRoute
        load={loadAdminLlmSettingsScreen}
        surfaceName="LLM settings"
        fallback={<AdminRouteLoadingFallback />}
        render={(AdminLlmSettingsScreen) => <AdminLlmSettingsScreen />}
      />
    );
  }

  return null;
}

function AdminRouteLoadingFallback() {
  return (
    <section aria-busy="true" className="max-w-[760px]">
      <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
        Admin
      </div>
      <h2 className="m-0 mt-1 text-[20px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        Loading LLM settings...
      </h2>
    </section>
  );
}

interface DecisionsScreenProps {
  view: ProjectDisplayData["decisionsHubView"];
  engineFixture: CarringtonEngineFixture | null;
  engineFixtureLoading: boolean;
  engineFixtureError: string | null;
}

interface QuoteScreenProps {
  projectId: string;
  projectName: string;
  generatedAt: string;
  outputBundle: ProjectDisplayData["projectFixture"]["outputBundle"];
  quotePricing: ProjectDisplayData["quotePricing"];
  partialScope?: { message: string } | null;
  onOpenProducts: () => void;
  readiness: ProjectReadinessV1 | null;
}

interface AgentTraceScreenProps {
  trace: ReturnType<typeof agentTraceForApartmentType>;
  onOpenDecisions: () => void;
}

interface MappingCommissioningScreenProps {
  view: ProjectDisplayData["mappingCommissioningView"];
  downstreamConfidence?: DownstreamConfidenceModel | null;
}

interface LiveCopilotPanelProps {
  projectData: ProjectDisplayData;
  currentWorkspaceView: ApartmentSolutionDisplayViewModel | null;
  engineFixture: CarringtonEngineFixture | null;
  engineFixtureLoading: boolean;
  engineFixtureError: string | null;
}

interface TenderIngestionScreenProps {
  projectData: ProjectDisplayData | null;
  selectedApartmentTypeId: string | null;
  intakeProject?: TenderIntakeProjectContext;
  engineFixture: CarringtonEngineFixture | null;
  engineFixtureLoading: boolean;
  engineFixtureError: string | null;
  initialTakeoffReview?: TenderTakeoffReviewState | null;
  onStartSpecify?: (
    input: SpecifyProjectInput,
    options?: { forceNew?: boolean; batchLabel?: string }
  ) => void;
  onContinueProjectSetup?: () => void;
}

interface DocumentsTakeoffsWorkspaceProps {
  projectId: string;
  docId: string | null;
  query: DocumentRouteQuery;
  onDocumentRoute: (input: {
    docId: string | null;
    query: DocumentRouteQuery;
    replace?: boolean;
  }) => void;
  onOpenInputs: () => void;
  onOpenCompare: () => void;
}

interface PreSpecificationWorkspaceProps {
  activeTab: SpecTab;
  canRunSpecify: boolean;
  disabledReason?: string | undefined;
  onTab: (tab: SpecTab) => void;
  onRunSpecify: () => void;
}

type QuoteRouteLoadingScreenProps = Omit<
  QuoteScreenProps,
  "onOpenProducts" | "readiness"
>;

const DecisionsScreen = lazy(async () => {
  const module = await import("./screens/DecisionsScreen");

  return {
    default: module.DecisionsScreen as ComponentType<DecisionsScreenProps>
  };
});

const PreSpecificationWorkspace = lazy(async () => {
  const module = await import("./screens/PreSpecificationWorkspace");

  return {
    default:
      module.PreSpecificationWorkspace as ComponentType<PreSpecificationWorkspaceProps>
  };
});

const AgentTraceScreen = lazy(async () => {
  const module = await import("./features/agent-trace/AgentTraceScreen");

  return {
    default: module.AgentTraceScreen as ComponentType<AgentTraceScreenProps>
  };
});

const MappingCommissioningScreen = lazy(async () => {
  const module = await import("./screens/MappingCommissioningScreen");

  return {
    default:
      module.MappingCommissioningScreen as ComponentType<MappingCommissioningScreenProps>
  };
});

const LiveCopilotPanel = lazy(async () => {
  const module = await import("./features/copilot/CopilotPanel");

  return {
    default: module.LiveCopilotPanel as ComponentType<LiveCopilotPanelProps>
  };
});

const loadQuoteScreen = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./screens/QuoteScreen.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as { QuoteScreen: ComponentType<QuoteScreenProps> })
      : await import("./screens/QuoteScreen");

  return {
    default: module.QuoteScreen as ComponentType<QuoteScreenProps>
  };
};

const QuoteRouteLoadingScreen = lazy(async () => {
  const module = await import("./screens/QuoteRouteLoadingScreen");

  return {
    default:
      module.QuoteRouteLoadingScreen as ComponentType<QuoteRouteLoadingScreenProps>
  };
});

const TenderIngestionScreen = lazy(async () => {
  const module = await import("./features/tender/TenderIngestionScreen");

  return {
    default: module.TenderIngestionScreen as ComponentType<TenderIngestionScreenProps>
  };
});

const DocumentsTakeoffsWorkspace = lazy(async () => {
  const module = await import("./features/documents/DocumentsTakeoffsWorkspace");

  return {
    default:
      module.DocumentsTakeoffsWorkspace as ComponentType<DocumentsTakeoffsWorkspaceProps>
  };
});

const DocumentViewerScreen = lazy(async () => {
  const module = await import("./screens/DocumentViewerScreen");

  return { default: module.DocumentViewerScreen };
});

const loadCompareMatrix = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./screens/spec/CompareMatrix.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as { CompareMatrix: ComponentType<CompareMatrixProps> })
      : await import("./screens/spec/CompareMatrix");

  return {
    default: module.CompareMatrix as ComponentType<CompareMatrixProps>
  };
};

const loadExportScreen = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./screens/ExportScreen.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as { ExportScreen: ComponentType<ExportScreenProps> })
      : await import("./screens/ExportScreen");

  return {
    default: module.ExportScreen as ComponentType<ExportScreenProps>
  };
};

const loadReadinessBanner = async (attempt: number) => {
  const module =
    import.meta.env.DEV && attempt > 0
      ? ((await import(
          /* @vite-ignore */ `./components/ReadinessBanner.tsx?tcsLazyRetry=${attempt.toString()}`
        )) as { ReadinessBanner: ComponentType<ReadinessBannerProps> })
      : await import("./components/ReadinessBanner");

  return {
    default: module.ReadinessBanner as ComponentType<ReadinessBannerProps>
  };
};

function ReadinessBannerFallback() {
  return (
    <div
      aria-live="polite"
      aria-busy="true"
      className="flex min-h-10 items-center gap-2 rounded-lg border border-[#E3C4B8] bg-[#FFF7F2] px-3 py-1.5"
    >
      <span className="h-2 w-2 rounded-full bg-[#A87518]" />
      <span className="text-[12px] font-extrabold text-[var(--ink)]">
        Loading issue readiness
      </span>
    </div>
  );
}

function QuoteRouteFallback(props: QuoteScreenProps) {
  const loadingProps: QuoteRouteLoadingScreenProps = {
    projectId: props.projectId,
    projectName: props.projectName,
    generatedAt: props.generatedAt,
    outputBundle: props.outputBundle,
    quotePricing: props.quotePricing
  };

  return (
    <Suspense fallback={<ProjectLoadingScreen error={null} surface="quote" />}>
      <QuoteRouteLoadingScreen {...loadingProps} />
    </Suspense>
  );
}

const loadingSurfaceFor = (screen: ScreenId) =>
  screen === "quote"
    ? "quote"
    : screen === "spec"
      ? "products"
      : screen === "requirements"
        ? "requirements"
        : "project";

interface ScreenRouterProps extends RequirementsSurfaceProps {
  screen: ScreenId;
  projectId: string | null;
  documentId: string | null;
  documentQuery: DocumentRouteQuery;
  mode: WorkspaceMode;
  projectData: ProjectDisplayData | null;
  tenderProject?: TenderIntakeProjectContext | null;
  workspaceData?: ProjectWorkspaceDisplayData | null;
  currentWorkspaceView: ApartmentSolutionDisplayViewModel | null;
  loadError: string | null;
  engineFixture: CarringtonEngineFixture | null;
  engineFixtureLoading: boolean;
  engineFixtureError: string | null;
  deliveredProjectNames: readonly string[];
  specTab: SpecTab;
  preSpecification?: {
    canRunSpecify: boolean;
    disabledReason?: string | undefined;
    onRunSpecify: () => void;
  } | null;
  requirementsStale?: boolean | undefined;
  costRollup?: ProjectCostTelemetryResponse | null;
  readiness?: ProjectReadinessV1 | null;
  policyFlagSet?: SpecPolicyFlagSet | null;
  sourceCoverageWarning?: SourceCoverageWarning | null;
  lazyRouteRecoveryConsumedWithoutStorage?: boolean | undefined;
  onMode: (mode: WorkspaceMode) => void;
  onNav: (screen: ScreenId) => void;
  onDocumentRoute: DocumentsTakeoffsWorkspaceProps["onDocumentRoute"];
  onOpenItem: (id: string | null) => void;
  onSelectApartmentType: (apartmentTypeId: string) => void;
  onSpecTab: (tab: SpecTab) => void;
  onRerunSpecification?: () => void;
  onRerunRequirements?: () => Promise<void>;
  onStartSpecify?: (
    input: SpecifyProjectInput,
    options?: { forceNew?: boolean; batchLabel?: string }
  ) => void;
  onContinueProjectSetup?: () => void;
  onCompareMatrixEdit: NonNullable<CompareMatrixProps["onEditCell"]>;
  onScheduleEntriesChanged?: (entries: readonly ScheduleEntry[]) => void;
  onReadinessWaiver?: (blocker: ReadinessBlockerV1, reason: string) => Promise<void>;
  onReadinessAnswer?: (
    blocker: ReadinessBlockerV1,
    answerReference: string
  ) => Promise<void>;
  onReadinessChanged?: (readiness: ProjectReadinessV1) => void;
}

export function ScreenRouter({
  screen,
  projectId,
  documentId,
  documentQuery,
  mode,
  projectData,
  tenderProject = null,
  workspaceData = null,
  currentWorkspaceView,
  loadError,
  engineFixture,
  engineFixtureLoading,
  engineFixtureError,
  deliveredProjectNames,
  specTab,
  preSpecification = null,
  requirementsStale = false,
  requirementsStage,
  requirementsArtifact,
  requirementsArtifactPath,
  costRollup = null,
  readiness = null,
  policyFlagSet = null,
  sourceCoverageWarning = null,
  lazyRouteRecoveryConsumedWithoutStorage = false,
  onMode,
  onNav,
  onDocumentRoute,
  onOpenItem,
  onSelectApartmentType,
  onSpecTab,
  onRerunSpecification,
  onRerunRequirements,
  onStartSpecify,
  onContinueProjectSetup,
  onCompareMatrixEdit,
  onScheduleEntriesChanged,
  onReadinessWaiver,
  onReadinessAnswer,
  onReadinessChanged
}: ScreenRouterProps) {
  const [downstreamConfidence, setDownstreamConfidence] =
    useState<DownstreamConfidenceModel | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!projectData || (screen !== "spec" && screen !== "mapping")) {
      setDownstreamConfidence(null);
      return () => {
        cancelled = true;
      };
    }

    setDownstreamConfidence(null);
    void import("./viewmodel/downstreamConfidence").then(
      ({ buildDownstreamConfidenceModel }) => {
        if (!cancelled) {
          setDownstreamConfidence(
            buildDownstreamConfidenceModel({
              projectData,
              readiness,
              policyFlagSet,
              activeApartmentTypeId: currentWorkspaceView?.apartmentTypeId ?? null
            })
          );
        }
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    currentWorkspaceView?.apartmentTypeId,
    policyFlagSet,
    projectData,
    readiness,
    screen
  ]);

  const availableWorkspaceData = projectData ?? workspaceData;

  const withReadiness = (content: ReactNode) => (
    <>
      <div className="flex-none px-5 pt-2 sm:px-7 lg:px-9">
        <RetryableLazyRoute
          load={loadReadinessBanner}
          surfaceName="Issue readiness"
          fallback={<ReadinessBannerFallback />}
          render={(ReadinessBanner) => (
            <ReadinessBanner
              readiness={readiness}
              {...(onReadinessWaiver ? { onWaive: onReadinessWaiver } : {})}
              {...(onReadinessAnswer ? { onAnswer: onReadinessAnswer } : {})}
              compact
            />
          )}
        />
      </div>
      {content}
    </>
  );

  if (screen === "spec") {
    if (preSpecification) {
      return (
        <Suspense fallback={<SpecWorkspaceLoadingScreen error={null} />}>
          <PreSpecificationWorkspace
            activeTab={specTab}
            canRunSpecify={preSpecification.canRunSpecify}
            disabledReason={preSpecification.disabledReason}
            onTab={onSpecTab}
            onRunSpecify={preSpecification.onRunSpecify}
          />
        </Suspense>
      );
    }

    if (!currentWorkspaceView || !availableWorkspaceData) {
      return <SpecWorkspaceLoadingScreen error={loadError} />;
    }

    // The raw `ApartmentSolutionForDisplay` `currentWorkspaceView` was derived from —
    // matched by apartmentTypeId — carries the engine's per-room logical zones / UFH
    // that the display VM hides. Passed through so the tabs can label rooms.
    const currentSolution = availableWorkspaceData.apartmentSolutions.find(
      (candidate) => candidate.apartmentTypeId === currentWorkspaceView.apartmentTypeId
    );

    return withReadiness(
      <SpecWorkspace
        mode={mode}
        view={currentWorkspaceView}
        {...(currentSolution ? { solution: currentSolution } : {})}
        outputBundle={availableWorkspaceData.projectFixture.outputBundle}
        quotePricing={availableWorkspaceData.quotePricing}
        advisories={availableWorkspaceData.projectFixture.fixture.advisories ?? null}
        partialScope={
          availableWorkspaceData.projectFixture.fixture.partialScope ?? null
        }
        draftScope={availableWorkspaceData.projectFixture.fixture.draftScope ?? null}
        projectId={availableWorkspaceData.projectFixture.project.projectId}
        apartmentTypes={availableWorkspaceData.workspaceViews.map((workspaceView) => ({
          apartmentTypeId: workspaceView.apartmentTypeId,
          label: workspaceView.title
        }))}
        matrix={availableWorkspaceData.matrixView}
        onMode={onMode}
        onOpenItem={onOpenItem}
        onOpenQuote={() => onNav("quote")}
        activeTab={specTab}
        onTab={onSpecTab}
        requirementsStale={requirementsStale}
        onRerunSpecification={onRerunSpecification}
        scheduleEntries={availableWorkspaceData.projectFixture.scheduleEntries ?? []}
        onScheduleEntriesChanged={onScheduleEntriesChanged}
        {...(downstreamConfidence ? { downstreamConfidence } : {})}
        policyFlagSet={policyFlagSet}
        disableRepeatedFailureReload={lazyRouteRecoveryConsumedWithoutStorage}
      />
    );
  }

  if (screen === "documents" && projectId) {
    return (
      <Suspense
        fallback={
          <div className="flex min-h-0 flex-1 items-center justify-center bg-[var(--surface)] text-[13px] font-bold text-[var(--muted)]">
            Loading document workspace…
          </div>
        }
      >
        <DocumentsTakeoffsWorkspace
          projectId={projectId}
          docId={documentId}
          query={documentQuery}
          onDocumentRoute={onDocumentRoute}
          onOpenInputs={() => onNav("inputs")}
          onOpenCompare={() => onNav("compare")}
        />
      </Suspense>
    );
  }

  if (screen === "viewer" && projectId) {
    return (
      <Suspense fallback={<ProjectLoadingScreen error={null} />}>
        <DocumentViewerScreen
          key={projectId}
          projectId={projectId}
          onOpenInputs={() => onNav("inputs")}
          onOpenCompare={() => onNav("compare")}
        />
      </Suspense>
    );
  }

  if (screen === "tender" && projectId) {
    const selectedWorkspaceView = currentWorkspaceView ?? projectData?.workspaceView;

    return (
      <Suspense fallback={<ProjectLoadingScreen error={null} />}>
        <TenderIngestionScreen
          key={projectId}
          projectData={projectData}
          selectedApartmentTypeId={selectedWorkspaceView?.apartmentTypeId ?? null}
          intakeProject={
            tenderProject ?? {
              projectId,
              projectName: projectData?.cockpitView.title ?? projectId
            }
          }
          engineFixture={engineFixture}
          engineFixtureLoading={engineFixtureLoading}
          engineFixtureError={engineFixtureError}
          initialTakeoffReview={null}
          {...(onStartSpecify ? { onStartSpecify } : {})}
          {...(onContinueProjectSetup ? { onContinueProjectSetup } : {})}
        />
      </Suspense>
    );
  }

  // Token usage, like Document Viewer and Tender above, is a self-contained
  // project surface: it reads the `costRollup` prop (sourced from
  // ProjectDetailResponse, independent of projectData) and does not depend
  // on a generated specification. B-TOKNAV: keep this ahead of the
  // `!projectData` guard below so an onboarding-status project routed here
  // (App.tsx's tenderScope carve-out) renders the real screen instead of
  // getting stuck on the loading fallback.
  if (screen === "tokenUsage") {
    return <TokenUsageScreen costRollup={costRollup} />;
  }

  if (!projectData) {
    return (
      <ProjectLoadingScreen
        error={loadError}
        surface={loadingSurfaceFor(screen)}
        {...(screen === "requirements"
          ? { onBackToViewer: () => onNav("viewer") }
          : {})}
      />
    );
  }

  if (screen === "overview") {
    return withReadiness(
      <ProjectOverview
        {...(projectId ? { projectId } : {})}
        cockpit={projectData.cockpitView}
        readiness={readiness}
        projectNames={deliveredProjectNames}
        onOpenSpec={(apartmentTypeId) => {
          onSelectApartmentType(apartmentTypeId);
          onOpenItem(null);
          onNav("spec");
        }}
        onNav={onNav}
      />
    );
  }

  if (screen === "inputs") {
    return (
      <InputsScreen
        view={projectData.requirementStudioView}
        selectedApartmentTypeId={
          currentWorkspaceView?.apartmentTypeId ??
          projectData.workspaceView.apartmentTypeId
        }
        onOpenTender={() => onNav("tender")}
      />
    );
  }

  if (screen === "requirements") {
    return (
      <RequirementsScreen
        view={projectData.requirementStudioView}
        {...(onRerunRequirements ? { onRerunStage: onRerunRequirements } : {})}
      />
    );
  }

  if (screen === "quote") {
    const quoteRouteProps: QuoteScreenProps = {
      projectId: projectData.projectFixture.project.projectId,
      projectName: projectData.cockpitView.title,
      generatedAt: projectData.projectFixture.fixture.generatedAt,
      outputBundle: projectData.projectFixture.outputBundle,
      quotePricing: projectData.quotePricing,
      partialScope: projectData.projectFixture.fixture.partialScope ?? null,
      readiness,
      onOpenProducts: () => onNav("spec")
    };

    return withReadiness(
      <RetryableLazyRoute
        load={loadQuoteScreen}
        surfaceName="Quote"
        onRepeatedFailureReload={
          lazyRouteRecoveryConsumedWithoutStorage
            ? undefined
            : () =>
                reloadForLazyRouteRecovery({
                  projectId: quoteRouteProps.projectId,
                  screen: "quote"
                })
        }
        fallback={<QuoteRouteFallback {...quoteRouteProps} />}
        render={(QuoteScreen) => <QuoteScreen {...quoteRouteProps} />}
      />
    );
  }

  if (screen === "compare") {
    return withReadiness(
      <RetryableLazyRoute
        load={loadCompareMatrix}
        surfaceName="Compare"
        fallback={<ProjectLoadingScreen error={null} />}
        render={(CompareMatrix) => (
          <CompareMatrix
            takeoffRows={projectData.projectFixture.takeoffRows}
            solutions={projectData.apartmentSolutions}
            projectId={projectData.projectFixture.project.projectId}
            partialScope={projectData.projectFixture.fixture.partialScope ?? null}
            overrides={projectData.projectFixture.fixture.compareMatrixOverrides ?? []}
            onEditCell={onCompareMatrixEdit}
            readiness={readiness}
            onRecalculateSpecification={onRerunSpecification}
            onOpenApartmentType={(apartmentTypeId) => {
              onSelectApartmentType(apartmentTypeId);
              onOpenItem(null);
              onNav("spec");
            }}
          />
        )}
      />
    );
  }

  if (screen === "copilot") {
    return (
      <Suspense fallback={<ProjectLoadingScreen error={null} />}>
        <LiveCopilotPanel
          projectData={projectData}
          currentWorkspaceView={currentWorkspaceView}
          engineFixture={engineFixture}
          engineFixtureLoading={engineFixtureLoading}
          engineFixtureError={engineFixtureError}
        />
      </Suspense>
    );
  }

  if (screen === "agentTrace") {
    return (
      <Suspense fallback={<ProjectLoadingScreen error={null} />}>
        <AgentTraceScreen
          trace={agentTraceForApartmentType(
            projectData,
            currentWorkspaceView?.apartmentTypeId ??
              projectData.workspaceView.apartmentTypeId
          )}
          onOpenDecisions={() => onNav("decisions")}
        />
      </Suspense>
    );
  }

  if (screen === "decisions") {
    return withReadiness(
      <Suspense fallback={<ProjectLoadingScreen error={null} surface="decisions" />}>
        <DecisionsScreen
          view={projectData.decisionsHubView}
          engineFixture={engineFixture}
          engineFixtureLoading={engineFixtureLoading}
          engineFixtureError={engineFixtureError}
        />
      </Suspense>
    );
  }

  if (screen === "risks") {
    return withReadiness(<RisksScreen view={projectData.riskLedgerView} />);
  }

  if (screen === "scope") {
    return withReadiness(<ScopeScreen view={projectData.scopeMatrixView} />);
  }

  if (screen === "mapping") {
    return withReadiness(
      <Suspense fallback={<ProjectLoadingScreen error={null} />}>
        <MappingCommissioningScreen
          view={projectData.mappingCommissioningView}
          downstreamConfidence={downstreamConfidence}
        />
      </Suspense>
    );
  }

  if (screen === "export") {
    return withReadiness(
      <RetryableLazyRoute
        load={loadExportScreen}
        surfaceName="Export"
        fallback={<ProjectLoadingScreen error={null} />}
        render={(ExportScreen) => (
          <ExportScreen
            view={projectData.exportPreviewView}
            requirementsStage={requirementsStage}
            requirementsArtifact={requirementsArtifact}
            requirementsArtifactPath={requirementsArtifactPath}
            {...(readiness ? { readiness } : {})}
            sourceCoverageWarning={sourceCoverageWarning}
            onOpenMapping={() => onNav("mapping")}
            onReadinessChanged={onReadinessChanged ?? (() => undefined)}
          />
        )}
      />
    );
  }

  return <StubScreen screen={screen} onOpenSpec={() => onNav("spec")} />;
}
